import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/axios';
import { useSubscription } from '../contexts/SubscriptionContext';

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function toTimeInputValue(isoInstant) {
  if (!isoInstant) return '';
  const d = new Date(isoInstant);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function toInstantOrNull(workDate, timeValue) {
  if (!timeValue) return null;
  return new Date(`${workDate}T${timeValue}:00`).toISOString();
}

// Story 20-A — owner manual punch record/correction. Mirrors AddLeaveModal.jsx's shape:
// a small modal, one row's worth of fields, subscription-gated save.
function ManualPunchModal({ open, onClose, row, workDate, onSaved }) {
  const { t } = useTranslation('attendance');
  const { t: tCommon } = useTranslation('common');
  const { isGated } = useSubscription();
  const [punchIn, setPunchIn] = useState('');
  const [punchOut, setPunchOut] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open || !row) return;
    setPunchIn(toTimeInputValue(row.punchInAt));
    setPunchOut(toTimeInputValue(row.punchOutAt));
    setError('');
    setSaving(false);
    setTimeout(() => firstFieldRef.current?.focus(), 50);
  }, [open, row]);

  if (!open || !row) return null;

  const isValid = Boolean(punchIn || punchOut);

  const handleSubmit = async () => {
    if (!isValid || isGated) return;
    setSaving(true);
    setError('');
    try {
      const res = await apiClient.post('/api/attendance/manual', {
        employeeId: row.employeeId,
        workDate,
        punchInAt: toInstantOrNull(workDate, punchIn),
        punchOutAt: toInstantOrNull(workDate, punchOut),
      });
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.message || t('modal.errors.actionFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Story 24 — manager's affirmative absence confirmation / its reversal. Both mirror
  // handleSubmit's shape: POST, then hand the fresh row back to onSaved.
  const handleMarkAbsent = async () => {
    if (isGated) return;
    setSaving(true);
    setError('');
    try {
      const res = await apiClient.post('/api/attendance/mark-absent', {
        employeeId: row.employeeId,
        workDate,
      });
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.message || t('modal.errors.markAbsentFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleClearAbsent = async () => {
    if (isGated) return;
    setSaving(true);
    setError('');
    try {
      const res = await apiClient.post('/api/attendance/clear-absent', {
        employeeId: row.employeeId,
        workDate,
      });
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.message || t('modal.errors.clearAbsentFailed'));
    } finally {
      setSaving(false);
    }
  };

  const canMarkAbsent = row.status === 'NO_PUNCH' || row.status === 'NOT_IN_YET';
  const canClearAbsent = row.status === 'ABSENT';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mp-modal-title"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-orange-100 flex flex-col overflow-hidden">
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h2 id="mp-modal-title" className="text-lg font-semibold text-gray-900">
              {t('modal.title')}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">{row.employeeName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="ml-4 text-gray-400 hover:text-gray-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-200 rounded"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <p className="text-xs text-gray-500 -mt-1">{t('modal.hint')}</p>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="mp-in" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('modal.fields.punchIn')}
              </label>
              <input
                ref={firstFieldRef}
                id="mp-in"
                type="time"
                value={punchIn}
                onChange={e => { setPunchIn(e.target.value); setError(''); }}
                disabled={saving}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500 disabled:opacity-60"
              />
            </div>
            <div>
              <label htmlFor="mp-out" className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('modal.fields.punchOut')}
              </label>
              <input
                id="mp-out"
                type="time"
                value={punchOut}
                onChange={e => { setPunchOut(e.target.value); setError(''); }}
                disabled={saving}
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 outline-none transition focus:ring-2 focus:ring-orange-200 focus:border-orange-500 disabled:opacity-60"
              />
            </div>
          </div>

          {(canMarkAbsent || canClearAbsent) && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">{t('modal.hintNoPunch')}</p>
              {canMarkAbsent && (
                <button
                  type="button"
                  onClick={handleMarkAbsent}
                  disabled={saving || isGated}
                  title={isGated ? tCommon('gatedAction.tooltip') : undefined}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving && <Spinner />}
                  {t('modal.markAbsent')}
                </button>
              )}
              {canClearAbsent && (
                <button
                  type="button"
                  onClick={handleClearAbsent}
                  disabled={saving || isGated}
                  title={isGated ? tCommon('gatedAction.tooltip') : undefined}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving && <Spinner />}
                  {t('modal.clearAbsent')}
                </button>
              )}
            </div>
          )}

          {isGated && (
            <p className="text-xs text-red-600" role="alert">{tCommon('gatedAction.tooltip')}</p>
          )}

          {error && (
            <p className="text-xs text-red-600" role="alert">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
          >
            {t('modal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || saving || isGated}
            title={isGated ? tCommon('gatedAction.tooltip') : undefined}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Spinner />}
            {t('modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManualPunchModal;
